import { StarFilled, StarOutlined } from "@ant-design/icons"
import { Tag, Tooltip, Typography } from "antd"
import { type CustomTagProps } from "rc-select/lib/BaseSelect"
import React from "react"

import {
  ResolvedSelect,
  type ResolvedSelectProps
} from "../../../../../../components/Select/ResolvedSelect.tsx"
import { UNRESOLVED_USER_LABEL } from "../../../../../../utils/select/entity-labels.ts"
import {
  hideEmptyOverflow,
  type OmittedValues
} from "../../../../../../utils/select/max-tag-placeholder.tsx"

const { Text } = Typography

interface Entity {
  id: string
  is_main: boolean
}

const overflowCounter = (omitted: OmittedValues): React.ReactNode => {
  const counted = hideEmptyOverflow(omitted)
  if (!counted) return null

  const names = omitted
    .map((manager) => String(manager?.label ?? "").trim())
    .filter(Boolean)
    .join(", ")

  return <Tooltip title={names}>{counted}</Tooltip>
}

interface ManagersSelectorProps
  extends Omit<ResolvedSelectProps, "value" | "onChange" | "mode"> {
  value?: Entity[]
  onChange?: (value: Entity[]) => void
}

export function ManagersSelector({
  value = [],
  onChange,
  className,
  maxTagCount,
  ...props
}: ManagersSelectorProps) {
  // Only the first chip survives a collapse, so the main manager has to lead —
  // otherwise the one name on show wears an empty star.
  const selectedIds = [...value]
    .sort((a, b) => Number(b.is_main) - Number(a.is_main))
    .map((m) => m.id)
  const mainManagerId = value.find((m) => m.is_main)?.id ?? null

  const handleManagersChange = (ids: string[]) => {
    const currentMainId = mainManagerId

    const newValue: Entity[] = ids.map((id) => ({
      id,
      is_main: currentMainId === id || (!currentMainId && ids[0] === id)
    }))

    if (currentMainId && !ids.includes(currentMainId) && ids.length > 0) {
      newValue[0].is_main = true
    }

    const mainCount = newValue.filter((m) => m.is_main).length
    if (mainCount > 1) {
      newValue.forEach((m, i) => {
        m.is_main = i === 0
      })
    }

    onChange?.(newValue)
  }

  const handleSetMain = (id: string) => {
    const newValue = value.map((m) => ({
      ...m,
      is_main: m.id === id
    }))
    onChange?.(newValue)
  }

  const tagRender = (tagProps: CustomTagProps) => {
    const { label, value: tagValue, closable, onClose, isMaxTag } = tagProps

    // The overflow counter comes through tagRender too, carrying no manager —
    // a star on it would promote `undefined`.
    if (isMaxTag) {
      return label ? (
        <Tag className={"max-w-full"} style={{ marginInlineEnd: 4 }}>
          {label}
        </Tag>
      ) : (
        <></>
      )
    }

    const isMain = tagValue === mainManagerId

    return (
      <Tag
        closable={closable}
        onClose={onClose}
        className={
          "flex gap-2 items-center max-w-full min-w-0 [&>.ant-tag-close-icon]:shrink-0"
        }
        style={{ marginInlineEnd: 4 }}
      >
        <div
          className={`shrink-0 ${props.disabled ? "" : "cursor-pointer"}`}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (props.disabled) return
            handleSetMain(tagValue as string)
          }}
        >
          <Tooltip title={isMain ? "Main manager" : "Set as main"}>
            {isMain ? (
              <StarFilled style={{ color: "#faad14" }} />
            ) : (
              <StarOutlined style={{ color: "#d9d9d9" }} />
            )}
          </Tooltip>
        </div>
        {/* rc-select wraps a custom tag in a bare span, so antd's own chip
            rules — which clamp and ellipsis the label — never reach it. */}
        <Text className={"min-w-0"} ellipsis={{ tooltip: label }}>
          {label}
        </Text>
      </Tag>
    )
  }

  return (
    <ResolvedSelect
      allowClear
      fallbackLabel={UNRESOLVED_USER_LABEL}
      mode="multiple"
      // Not "responsive": it measures the chip against the control, so a name
      // long enough to fill a narrow field is dropped whole, counted and never
      // shown.
      maxTagCount={maxTagCount}
      maxTagPlaceholder={overflowCounter}
      value={selectedIds}
      onChange={handleManagersChange}
      tagRender={tagRender}
      filterOption={(input, option) =>
        ((option?.label as string) ?? "")
          ?.toLowerCase()
          .includes(input.toLowerCase())
      }
      {...props}
      // After the spread: these carry the stylesheet rules that keep a chip
      // inside the field, so a caller passing className must not lose them.
      // Nowrap without a counter to absorb what it squeezes out would shrink
      // every chip, hence the second is derived rather than set by hand.
      className={[
        "managers-select",
        maxTagCount === undefined ? null : "managers-select-inline",
        className
      ]
        .filter(Boolean)
        .join(" ")}
    />
  )
}
